import styled, { css } from 'styled-components';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { Button, Glyphicon, Popover } from 'react-bootstrap';

import theme from 'styles/theme';
import { htmlMessageWrapperId } from './RichTextEditor.constants';

const ContentEditableCommonStyles = css`
  min-height: 150px;
  resize: vertical;
  position: relative;
  tab-size: 1;
  outline: 0;
  padding: 15px 10px;
  caret-color: #444;
  margin-bottom: 20px;

  .editor-container {
    margin: 20px auto 20px auto;
    border-radius: 2px;
    max-width: 600px;
    color: #000;
    position: relative;
    line-height: 20px;
    font-weight: 400;
    text-align: left;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }

  .editor-inner {
    background: #fff;
    position: relative;
  }

  .editor-input {
    min-height: 150px;
    resize: none;
    font-size: 15px;
    caret-color: rgb(5, 5, 5);
    position: relative;
    tab-size: 1;
    outline: 0;
    padding: 15px 10px;
    caret-color: #444;
  }

  .editor-placeholder {
    color: #999;
    overflow: hidden;
    position: absolute;
    text-overflow: ellipsis;
    top: 15px;
    left: 10px;
    font-size: 15px;
    user-select: none;
    display: inline-block;
    pointer-events: none;
  }

  .editor-text-bold {
    font-weight: bold;
  }

  .editor-text-italic {
    font-style: italic;
  }

  .editor-text-underline {
    text-decoration: underline;
  }

  .editor-text-strikethrough {
    text-decoration: line-through;
  }

  .editor-text-underlineStrikethrough {
    text-decoration: underline line-through;
  }

  .editor-text-code {
    background-color: rgb(240, 242, 245);
    padding: 1px 0.25rem;
    font-family: Menlo, Consolas, Monaco, monospace;
    font-size: 94%;
  }

  .editor-link {
    color: rgb(33, 111, 219);
    text-decoration: none;
  }

  .editor-code {
    background-color: rgb(240, 242, 245);
    font-family: Menlo, Consolas, Monaco, monospace;
    display: block;
    padding: 8px 8px 8px 52px;
    line-height: 1.53;
    font-size: 13px;
    margin: 0;
    margin-top: 8px;
    margin-bottom: 8px;
    tab-size: 2;
    overflow-x: auto;
    position: relative;
  }

  .editor-code:before {
    content: attr(data-gutter);
    position: absolute;
    background-color: #eee;
    left: 0;
    top: 0;
    border-right: 1px solid #ccc;
    padding: 8px;
    color: #777;
    white-space: pre-wrap;
    text-align: right;
    min-width: 25px;
  }
  .editor-code:after {
    content: attr(data-highlight-language);
    top: 0;
    right: 3px;
    padding: 3px;
    font-size: 10px;
    text-transform: uppercase;
    position: absolute;
    color: rgba(0, 0, 0, 0.5);
  }

  .editor-tokenComment {
    color: slategray;
  }

  .editor-tokenPunctuation {
    color: #999;
  }

  .editor-tokenProperty {
    color: #905;
  }

  .editor-tokenSelector {
    color: #690;
  }

  .editor-tokenOperator {
    color: #9a6e3a;
  }

  .editor-tokenAttr {
    color: #07a;
  }

  .editor-tokenVariable {
    color: #e90;
  }

  .editor-tokenFunction {
    color: #dd4a68;
  }

  .editor-paragraph {
    margin: 0;
    margin-bottom: 8px;
    position: relative;
  }

  .editor-paragraph:last-child {
    margin-bottom: 0;
  }

  .editor-heading-h1 {
    font-size: 24px;
    color: rgb(5, 5, 5);
    font-weight: 400;
    margin: 0;
    margin-bottom: 12px;
    padding: 0;
  }

  .editor-heading-h2 {
    font-size: 15px;
    color: rgb(101, 103, 107);
    font-weight: 700;
    margin: 0;
    margin-top: 10px;
    padding: 0;
    text-transform: uppercase;
  }

  .editor-quote {
    margin: 0;
    margin-left: 20px;
    font-size: 15px;
    color: rgb(101, 103, 107);
    border-left-color: rgb(206, 208, 212);
    border-left-width: 4px;
    border-left-style: solid;
    padding-left: 16px;
  }

  .editor-list-ol {
    padding: 0;
    margin: 0;
    margin-left: 16px;
  }

  .editor-list-ul {
    padding: 0;
    margin: 0;
    margin-left: 16px;
  }

  .editor-listitem {
    margin: 8px 32px 8px 32px;
  }

  .editor-nested-listitem {
    list-style-type: none;
  }
`;

export const ContentEditableStyled = styled(ContentEditable).attrs<{
  id: string;
}>((props) => ({
  className: 'editable-area',
  'data-testid': props.id,
}))`
  ${ContentEditableCommonStyles}
`;

export const ContentPreviewStyled = styled.div.attrs({
  className: 'editable-area',
})`
  ${ContentEditableCommonStyles}
`;

export const RichTextEditorContainer = styled.div.attrs<{
  id: string;
  $isDisabled: boolean;
}>((props) => ({ 'data-testid': `${props.id}` }))`
  border: 1px solid #ddd;
  border-radius: 5px;
  position: relative;
  ${({ $isDisabled }) => $isDisabled && 'background-color: #E9ECEF;'}

  div#${htmlMessageWrapperId} {
    margin-top: 0.8em; // prevents overlapping the 'clear editor' button
    white-space: normal; // required for rendering message templates properly
  }

  .editable-area > p {
    margin: 0 0 10px;
  }
`;

export const RichTextEditorInnerContainer = styled.div`
  position: relative;
`;

export const RichTextEditorPlaceholder = styled.div`
  color: #999;
  font-weight: 300;
  overflow: hidden;
  position: absolute;
  text-overflow: ellipsis;
  top: 15px;
  left: 10px;
  user-select: none;
  display: inline-block;
  pointer-events: none;
`;

export const RichTextEditorControlsWrapper = styled.div.attrs({
  'data-testid': 'rich-text-editor-controls-wrapper',
})<{ $isSticky?: boolean }>`
  display: flex;
  justify-content: flex-end;
  padding: 0px 1em 1em 1em;

  ${({ $isSticky }) =>
    $isSticky &&
    `
      background-color: white;
      border-bottom-left-radius: 5px;
      border-bottom-right-radius: 5px;
      bottom: 0;
      padding-top: 1em;
      position: sticky;
      z-index: 999;

      &.is-stuck {
        --shadow-color: 0deg 0% 80%;
        box-shadow: 0px -0.3px 0.3px hsl(var(--shadow-color) / 0.18),
          0px -0.9px 1px -0.8px hsl(var(--shadow-color) / 0.18),
          0px -2px 2.3px -1.7px hsl(var(--shadow-color) / 0.18),
          0.1px -4.9px 5.5px -2.5px hsl(var(--shadow-color) / 0.18);
      }
  `}
`;

export const RichTextEditorControlsLoader = styled.div.attrs({
  'data-testid': 'rich-text-editor-controls-loader',
})`
  align-items: center;
  display: flex;
  justify-content: flex-start;
`;

export const RichTextEditorControls = styled.div.attrs({
  'data-testid': 'rich-text-editor-controls',
})`
  align-items: center;
  display: flex;
  justify-content: flex-end;
`;

export const RichTextEditorToolbarStyled = styled.div`
  align-self: center;
  background-color: ${theme.colors.light};
  border-bottom: 1px solid #ced4da;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  padding: 5px 10px;

  .dropdown:has(.toolbar-item--horizontal-dropdown).open {
    [role='menu'].dropdown-menu {
      align-items: center;
      display: flex;
      gap: 5px;
      min-width: 0;
      padding: 0;

      [role='menuitem'] {
        align-items: center;
        display: flex;
        padding: 5px;
      }
    }
  }

  .dropdown:has(.toolbar-item--vertical-dropdown).open {
    [role='menu'].dropdown-menu {
      max-height: 200px;
      overflow-y: auto;
    }
  }

  .color-picker-column {
    align-items: center;
    display: flex;
    flex-direction: column;
    font-size: 0.85rem;
    gap: 10px;
    padding: 10px;

    > .btn {
      align-self: stretch;
      padding: 0;
      font-size: 0.85rem;
    }
  }

  .toolbar-item {
    align-items: center;
    display: flex;
    gap: 5px;
    height: 25px;
    justify-content: center;
    min-width: 25px;
    padding: 5px;

    &.toolbar-item--green,
    &.toolbar-item--green:focus,
    &.toolbar-item--green:hover {
      background-color: ${theme.colors.bgGreen};
      color: white;
    }

    &.toolbar-item--large {
      min-width: 40px;
    }

    .bold {
      font-weight: bold;
    }

    .italic {
      font-style: italic;
    }

    .strikethrough {
      text-decoration: line-through;
    }

    .underline {
      text-decoration: underline;
    }
  }
`;

export const RichTextEditorEditLinkPopover = styled(Popover)`
  max-width: 400px;

  .popover-content {
    align-items: center;
    display: flex;
    gap: 5px;

    > a {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .btn-group {
    flex-shrink: 0;
  }

  .btn-group,
  .btn-group .btn {
    align-items: center;
    align-self: stretch;
    display: flex;
  }
`;

export const RichTextEditorHTMLTextarea = styled.textarea`
  background-color: #212121;
  color: #cccccc;
  padding: 10px;
  resize: vertical;
  width: 100%;
`;

export const RichTextEditorControlsAttachmentIconContainer = styled.label<{
  htmlFor: string;
}>`
  margin-bottom: 0;

  input[type='file'] {
    display: none;
  }
`;
export const RichTextEditorControlsAttachmentIcon = styled(Glyphicon).attrs({
  'data-testid': 'rich-text-editor-attachment-icon',
  glyph: 'paperclip',
})<{ disabled: boolean }>`
  font-size: 1.2em;
  margin-right: 0.2em;
  padding: 0.5em;
  color: ${(props) => (props.disabled ? '#ccc' : 'inherit')};

  input[type='file'] {
    display: none;
  }

  ${(props) =>
    !props.disabled &&
    `
    cursor: pointer;
    
    &:hover {
      background-color: #f5f5f5;
      border-radius: 50%;
    }
  `}
`;

export const RichTextEditorSuggestionButton = styled(Button)`
  margin-right: 0.2em;
`;
